import { api } from '../api.ts';

/**
 * The evidence-service client — the OTHER side of the seam.
 *
 * The dashboard never holds a standing evidence credential: it exchanges the
 * EVAC session for a ten-minute ES256 token scoped to the org
 * (`POST /orgs/:id/evidence/token`), and talks to the separately-scoped
 * evidence service with that. The service's address arrives WITH the token —
 * the dashboard is not configured with it, the identity core is.
 *
 * Two refusals are product states, not errors, and callers branch on them:
 * - 501 from the exchange       → the seam is not configured in this
 *                                 environment: the module says so quietly.
 * - 403 `evidence not enabled…` → onboarding: an admin can turn it on.
 *
 * And one is a first-class flow: 428 from the service is the
 * reason-for-access prompt (evidence spec 03 §1.3) — sensitive reads REQUIRE
 * a stated reason, the screen asks for one, and the reason travels on every
 * subsequent read this session. That is a feature (the reason lands in the
 * audit record read back to auditors), never an error to swallow.
 */

export interface EvidenceExchange {
  token: string;
  expiresAt: number;
  serviceUrl: string;
}

export interface ReasonChallenge {
  header: string;
  classes: string[];
  free_text_header: string;
  free_text_limit: number;
}

export class EvidenceReasonRequired extends Error {
  constructor(readonly challenge: ReasonChallenge) {
    super('a reason for access is required');
  }
}

export class EvidenceError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

interface Grant {
  token: string;
  expiresAt: number;
  serviceUrl: string;
}

let grant: Grant | null = null;
let grantOrg: string | null = null;

/** The stated reason travels with every sensitive read this session. */
let reason: { cls: string; text?: string } | null = null;

export function setAccessReason(cls: string, text?: string): void {
  reason = { cls, ...(text ? { text } : {}) };
}

export function clearEvidenceSession(): void {
  grant = null;
  grantOrg = null;
  reason = null;
}

async function grantFor(orgId: string): Promise<Grant> {
  if (grant !== null && grantOrg === orgId && grant.expiresAt - 30_000 > Date.now()) {
    return grant;
  }
  const exchange = await api<EvidenceExchange>(`/orgs/${orgId}/evidence/token`, {
    method: 'POST',
  });
  if (exchange === null) throw new EvidenceError(500, 'empty token exchange');
  grant = exchange;
  grantOrg = orgId;
  return exchange;
}

export async function evidenceApi<T>(
  orgId: string,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const g = await grantFor(orgId);
  const headers: Record<string, string> = {
    authorization: `Bearer ${g.token}`,
    ...(init.headers as Record<string, string> | undefined),
  };
  if (init.body) headers['content-type'] = 'application/json';
  if (reason !== null) {
    headers['x-access-reason'] = reason.cls;
    if (reason.text) headers['x-access-reason-text'] = reason.text;
  }
  const res = await fetch(`${g.serviceUrl}${path}`, { ...init, headers });
  if (res.status === 428) {
    const body = (await res.json()) as { challenge: ReasonChallenge };
    throw new EvidenceReasonRequired(body.challenge);
  }
  if (res.status === 401) {
    // The ten-minute token lapsed mid-session: one fresh exchange, one retry.
    grant = null;
    const g2 = await grantFor(orgId);
    const retry = await fetch(`${g.serviceUrl}${path}`, {
      ...init,
      headers: { ...headers, authorization: `Bearer ${g2.token}` },
    });
    if (!retry.ok) throw new EvidenceError(retry.status, await errText(retry));
    return (await retry.json()) as T;
  }
  if (!res.ok) throw new EvidenceError(res.status, await errText(res));
  return (await res.json()) as T;
}

async function errText(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string; message?: string };
    return body.message ?? body.error ?? res.statusText;
  } catch {
    return res.statusText;
  }
}

/**
 * The authorized bytes of an asset, fetched through the same token seam as
 * every other read — Bearer grant, the stated reason travelling on the headers
 * (a view of the media IS a sensitive read and belongs in the audit record) and
 * one silent re-exchange on a 401.
 *
 * Returns the raw `Blob`. The caller renders it from an in-memory `blob:` URL,
 * so no `<img>`/`<video>` src is ever a cross-origin URL — the strict CSP is
 * honoured and the only host ever contacted is the evidence service the grant
 * itself names.
 */
async function evidenceBytes(orgId: string, path: string): Promise<Blob> {
  const g = await grantFor(orgId);
  const headers: Record<string, string> = { authorization: `Bearer ${g.token}` };
  if (reason !== null) {
    headers['x-access-reason'] = reason.cls;
    if (reason.text) headers['x-access-reason-text'] = reason.text;
  }
  const res = await fetch(`${g.serviceUrl}${path}`, { headers });
  if (res.status === 428) {
    const body = (await res.json()) as { challenge: ReasonChallenge };
    throw new EvidenceReasonRequired(body.challenge);
  }
  if (res.status === 401) {
    grant = null;
    const g2 = await grantFor(orgId);
    const retry = await fetch(`${g.serviceUrl}${path}`, {
      headers: { ...headers, authorization: `Bearer ${g2.token}` },
    });
    if (!retry.ok) throw new EvidenceError(retry.status, await errText(retry));
    return retry.blob();
  }
  if (!res.ok) throw new EvidenceError(res.status, await errText(res));
  return res.blob();
}

// ---- shapes the evidence service returns -----------------------------------

export interface EvidenceAsset {
  id: string;
  state: 'quarantined' | 'stored' | 'voided' | 'sealed_illegal' | 'disposed';
  /**
   * The honest grade (spec §1.5). `captured` is device-attested; `declared` is
   * a third-party upload (a browser recording or file), where the platform
   * guarantees integrity from ingest and the provenance is as the uploader
   * stated — never more. `derived` is a child asset the platform itself
   * produced from a parent (e.g. a redaction derivative, spec §6.2) — its
   * lineage is the parent plus the recorded transform, not a fresh capture.
   * Surfaced in the inbox so the grade is legible at a glance, not buried in
   * the detail view. The `/v1/assets` list has always returned this column;
   * the type simply names it.
   */
  provenance_grade: 'captured' | 'declared' | 'derived';
  media_type: string;
  bytes: number;
  merkle_root: string;
  captured_at: string;
  committed_at: string | null;
  created_at: string;
  /** Present on the inbox: a live legal hold (on the asset or a case it is in). */
  under_legal_hold?: boolean;
}

/** The effective legal hold on an asset — on the asset itself or via a case. */
export interface EffectiveHold {
  held: boolean;
  via: 'asset' | 'case' | null;
  reason: string | null;
  case_id: string | null;
  placed_by: string | null;
  placed_at: string | null;
}

export interface CustodyEvent {
  actor: string;
  actor_type: string;
  action: string;
  /**
   * The operator note ('annotated') or revoke reason ('disclosure_revoked')
   * carried on the event. A chained column on the service side, so what shows
   * here is exactly what the custody hash covers.
   */
  justification: string | null;
  at: string;
  detail: Record<string, unknown> | null;
}

export interface EvidenceManifest {
  id: string;
  kind: string;
  canonical: unknown;
  manifest_hash: string;
  log_leaf_index: number | null;
  created_at: string;
  signatures: { role: string; alg: string; key_id: string; sig: string }[];
}

export interface AssetDetail {
  asset: EvidenceAsset & {
    provenance_grade: 'captured' | 'declared' | 'derived';
    duration_s: number | null;
    retain_until: string | null;
    device_ref: { device_id?: string; firmware?: string | null };
  };
  custody: CustodyEvent[];
  manifests: EvidenceManifest[];
  legal_hold: EffectiveHold;
}

export interface Sth {
  tree_size: number;
  root_hash: string;
  signed_at: string;
  key_id: string;
  sth_sig: string;
  witness_cosigs: unknown[];
}

/**
 * The server-side filters the inbox understands (findings #14/#15/#19). All
 * optional; an absent field is "no filter on this dimension". The service
 * applies them tenant-scoped, index-backed, across every page — so a filter
 * reaches records that have not been loaded yet, not just the rows on screen.
 */
export interface AssetFilters {
  state?: EvidenceAsset['state'];
  mediaType?: string;
  /** ISO-8601 (with offset) lower bound on captured_at, inclusive. */
  capturedAfter?: string;
  /** ISO-8601 (with offset) upper bound on captured_at, inclusive. */
  capturedBefore?: string;
  /** Only assets currently linked into this case. */
  caseId?: string;
}

/**
 * One page of the evidence inbox. `items` is the standard list key (finding
 * #20). `next_cursor` is an opaque token: pass it back to `listAssets` for the
 * next page; it is `null` at the end. `total` is every asset the tenant holds
 * and `filtered_total` how many match the active filters (equal when
 * unfiltered) — so the inbox shows a TRUE total, not "loaded so far". The
 * service caps a page at 200 rows and stays tenant-scoped on every page.
 */
export interface AssetPage {
  items: EvidenceAsset[];
  next_cursor: string | null;
  total: number;
  filtered_total: number;
}

export const listAssets = (orgId: string, cursor?: string | null, filters: AssetFilters = {}) => {
  const q = new URLSearchParams();
  if (filters.state !== undefined) q.set('state', filters.state);
  if (filters.mediaType !== undefined) q.set('media_type', filters.mediaType);
  if (filters.capturedAfter !== undefined) q.set('captured_after', filters.capturedAfter);
  if (filters.capturedBefore !== undefined) q.set('captured_before', filters.capturedBefore);
  if (filters.caseId !== undefined) q.set('case_id', filters.caseId);
  if (cursor !== undefined && cursor !== null) q.set('cursor', cursor);
  const qs = q.toString();
  return evidenceApi<AssetPage>(orgId, qs === '' ? '/v1/assets' : `/v1/assets?${qs}`);
};

export const getAsset = (orgId: string, id: string) =>
  evidenceApi<AssetDetail>(orgId, `/v1/assets/${id}`);

export const getReceipt = (orgId: string, id: string) =>
  evidenceApi<{ assetId: string; receipt: unknown; sig: Record<string, string> }>(
    orgId,
    `/v1/assets/${id}/receipt`,
  );

// ---- custody annotations ---------------------------------------------------

export interface Annotation {
  id: string;
  asset_id: string;
  author: string;
  note: string;
  at: string;
  /** hex chain hash of the appended custody event. */
  event_hash: string;
}

/**
 * Append an operator note to an asset. It is recorded as an append-only
 * 'annotated' custody event — it does NOT change the asset, its media, or the
 * existing custody hash chain. The dashboard says as much where it is offered.
 */
export const addAnnotation = (orgId: string, assetId: string, note: string) =>
  evidenceApi<{ annotation: Annotation }>(orgId, `/v1/assets/${assetId}/annotations`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });

// ---- read-audit trail ------------------------------------------------------

/** One recorded access, as the service stored it — reason null when none was stated. */
export interface AccessRecord {
  event_id: string;
  occurred_at: string;
  completed_at: string | null;
  actor_type: string;
  actor_id: string;
  actor_display: string;
  action: string;
  resource_class: string;
  resource_id: string;
  outcome: string;
  reason_class: string | null;
  reason: string | null;
  ip: string | null;
  request_id: string;
}

export interface AccessPage {
  records: AccessRecord[];
  nextCursor: string | null;
}

/**
 * The tenant-scoped read-audit trail (who accessed what, when, and why). The
 * service scopes it to the caller's org and requires a stated reason — reading
 * the audit trail is itself an audited access. Bounded + keyset-paginated;
 * pass the previous page's `nextCursor` to continue.
 */
export const listAccess = (
  orgId: string,
  opts: {
    assetId?: string | undefined;
    limit?: number | undefined;
    cursor?: string | undefined;
  } = {},
) => {
  const q = new URLSearchParams();
  if (opts.assetId !== undefined) q.set('assetId', opts.assetId);
  if (opts.limit !== undefined) q.set('limit', String(opts.limit));
  if (opts.cursor !== undefined) q.set('cursor', opts.cursor);
  const qs = q.toString();
  return evidenceApi<AccessPage>(orgId, `/v1/audit/access${qs ? `?${qs}` : ''}`);
};

/* ----------------------------------------------------------------- preview */

/**
 * The ORIGINAL media bytes of an asset — the bytes the content hash is over,
 * not a derived thumbnail. The dashboard renders these as-is (no pixels
 * altered) and a download saves these same bytes; either way the artefact the
 * viewer sees is the verified original, never a re-encoding of it. The content
 * path mirrors the `/v1/assets/:id` metadata path the rest of this client
 * speaks.
 */
export const getAssetContent = (orgId: string, id: string): Promise<Blob> =>
  evidenceBytes(orgId, `/v1/assets/${id}/content`);

export type PreviewKind = 'image' | 'video';

/**
 * The ceiling on inline preview. Above it, the screen offers the original for
 * download rather than pulling a large recording fully into memory — a preview
 * is a convenience and must never be the reason a browser tab dies.
 */
export const PREVIEW_MAX_BYTES = 25 * 1024 * 1024;

/** The kind of inline preview a media type supports, or null for none. */
export function previewKind(mediaType: string): PreviewKind | null {
  if (mediaType.startsWith('image/')) return 'image';
  if (mediaType.startsWith('video/')) return 'video';
  return null;
}

/** Whether this asset can be shown inline: a previewable type, within the size ceiling. */
export function isPreviewable(asset: { media_type: string; bytes: number }): boolean {
  return previewKind(asset.media_type) !== null && asset.bytes <= PREVIEW_MAX_BYTES;
}

const EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
};

/** A sensible download extension for a media type — the subtype is the fallback. */
export function extensionFor(mediaType: string): string {
  return EXTENSIONS[mediaType] ?? mediaType.split('/')[1]?.split(';')[0] ?? 'bin';
}

/** Public — no token needed; fetched from the service the grant names. */
export async function getSth(orgId: string): Promise<Sth> {
  const g = await grantFor(orgId);
  const res = await fetch(`${g.serviceUrl}/verify/sth`);
  if (!res.ok) throw new EvidenceError(res.status, await errText(res));
  return (await res.json()) as Sth;
}

export async function getProof(
  orgId: string,
  leaf: number,
  size: number,
): Promise<{ leaf_index: number; tree_size: number; path: string[] }> {
  const g = await grantFor(orgId);
  const res = await fetch(`${g.serviceUrl}/verify/proof?leaf=${leaf}&size=${size}`);
  if (!res.ok) throw new EvidenceError(res.status, await errText(res));
  return (await res.json()) as { leaf_index: number; tree_size: number; path: string[] };
}

export async function getKeys(orgId: string): Promise<{ keys: unknown[] }> {
  const g = await grantFor(orgId);
  const res = await fetch(`${g.serviceUrl}/verify/keys`);
  if (!res.ok) throw new EvidenceError(res.status, await errText(res));
  return (await res.json()) as { keys: unknown[] };
}

/**
 * The downloadable verification bundle — the same shape the offline verifier
 * CLI reads (evidence repo `scripts/verify-evidence.ts`): manifest, its
 * signatures, the inclusion proof under the current signed tree head, and
 * the signing keys. Built client-side from what the service serves, so the
 * bundle a customer hands their lawyer is assembled in front of them.
 */
export async function buildVerificationBundle(
  orgId: string,
  manifest: EvidenceManifest,
): Promise<Record<string, unknown>> {
  if (manifest.log_leaf_index === null) {
    throw new EvidenceError(
      409,
      'this manifest has not been anchored to the log yet — try shortly',
    );
  }
  const sth = await getSth(orgId);
  const proof = await getProof(orgId, manifest.log_leaf_index, sth.tree_size);
  const keys = await getKeys(orgId);
  return {
    manifest: manifest.canonical,
    manifest_created_at: manifest.created_at,
    signatures: manifest.signatures,
    leaf_index: manifest.log_leaf_index,
    inclusion_path: proof.path,
    sth,
    keys: keys.keys,
  };
}

// ---- lifecycle & safety ----------------------------------------------------
// Retention/disposal PREVIEW (read-only), legal holds, and stuck-ingest recovery.
// The disposal preview never deletes; there is no disposal-execution call here,
// by design — the service exposes none.

export interface RetentionAssetPreview {
  id: string;
  state: string;
  media_type: string;
  bytes: number;
  captured_at: string;
  committed_at: string | null;
  retain_until: string | null;
  past_retention: boolean;
  under_legal_hold: boolean;
  hold_via: 'asset' | 'case' | null;
  hold_reason: string | null;
  eligible_for_disposal: boolean;
}

export interface RetentionPreview {
  preview: true;
  note: string;
  as_of: string;
  policy: { retain_min_years: number };
  summary: {
    stored: number;
    past_retention: number;
    eligible_for_disposal: number;
    blocked_by_legal_hold: number;
  };
  assets: RetentionAssetPreview[];
}

/** Read-only disposal preview. Deletes nothing; every response says so. */
export const retentionPreview = (orgId: string, asOf?: string) =>
  evidenceApi<RetentionPreview>(
    orgId,
    `/v1/retention/preview${asOf ? `?as_of=${encodeURIComponent(asOf)}` : ''}`,
  );

export interface LegalHold {
  id: string;
  asset_id: string | null;
  case_id: string | null;
  reason: string;
  placed_by: string;
  placed_at: string;
  released_at: string | null;
  released_by: string | null;
  release_reason: string | null;
}

export const placeAssetHold = (orgId: string, assetId: string, reason: string) =>
  evidenceApi<{ hold: LegalHold }>(orgId, `/v1/assets/${assetId}/hold`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });

export const releaseAssetHold = (orgId: string, assetId: string, reason?: string) =>
  evidenceApi<{ hold: LegalHold }>(orgId, `/v1/assets/${assetId}/hold/release`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });

export interface DeadLetterRecord {
  status: 'parked' | 'awaiting_reupload' | 'resolved';
  retries: number;
  last_error: string | null;
}

export interface StuckIngest {
  session_id: string;
  merkle_root: string;
  state: string;
  source: string;
  created_at: string;
  expires_at: string;
  reason: string;
  dead_letter: DeadLetterRecord | null;
}

export const listStuckIngests = (orgId: string) =>
  evidenceApi<{ stuck: StuckIngest[] }>(orgId, '/v1/ingest/stuck');

export interface StuckRetryResult {
  resolved: boolean;
  status: 'parked' | 'awaiting_reupload' | 'resolved';
  asset_id: string | null;
  error: string | null;
}

export const retryStuckIngest = (orgId: string, sessionId: string) =>
  evidenceApi<StuckRetryResult>(orgId, `/v1/ingest/stuck/${sessionId}/retry`, { method: 'POST' });
